import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { StoreResponse, StoreResponseError, StoreResponseSuccess } from '@p2p/shared/domain';
import { map, Observable, take } from 'rxjs';

import { CommonSettingsUnionKeys, PaymentMethodSetting } from '../entities';

import * as PlatformSettingsActions from './platform-settings.actions';
import * as PlatformSettingsSelectors from './platform-settings.selectors';

@Injectable()
export class PlatformSettingsFacade {
    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);

    paymentMethodsLoading$ = this.store.select(PlatformSettingsSelectors.selectPaymentMethodsLoading);
    paymentMethodsLoaded$ = this.store.select(PlatformSettingsSelectors.selectPaymentMethodsLoaded);
    paymentMethodsData$ = this.store.select(PlatformSettingsSelectors.selectPaymentMethodsData);
    paymentMethodsError$ = this.store.select(PlatformSettingsSelectors.selectPaymentMethodsError);

    commonLoading$ = this.store.select(PlatformSettingsSelectors.selectCommonLoading);
    commonLoaded$ = this.store.select(PlatformSettingsSelectors.selectCommonLoaded);
    commonData$ = this.store.select(PlatformSettingsSelectors.selectCommonData);
    commonError$ = this.store.select(PlatformSettingsSelectors.selectCommonError);

    loadPaymentMethodSettings(): void {
        this.store.dispatch(PlatformSettingsActions.loadPaymentMethods());
    }

    loadCommonSettings(): void {
        this.store.dispatch(PlatformSettingsActions.loadCommon());
    }

    updatePaymentMethodSettings(paymentMethodSettings: PaymentMethodSetting[]): void {
        this.store.dispatch(PlatformSettingsActions.updatePaymentMethods({ paymentMethodSettings }));
    }

    updateCommonSettings(commonSettings: CommonSettingsUnionKeys): void {
        this.store.dispatch(PlatformSettingsActions.updateCommon({ commonSettings }));
    }

    get updatePaymentMethodSettingsResponse$(): Observable<StoreResponse<void>> {
        return this.actions$.pipe(
            ofType(
                PlatformSettingsActions.updatePaymentMethodsSuccess,
                PlatformSettingsActions.updatePaymentMethodsFailure
            ),
            map((action) => {
                let result: StoreResponse<void> = { status: 'error' };

                if (action.type === PlatformSettingsActions.updatePaymentMethodsSuccess.type) {
                    result = new StoreResponseSuccess<void>();
                }

                if (action.type === PlatformSettingsActions.updatePaymentMethodsFailure.type) {
                    result = new StoreResponseError(action.error);
                }

                return result;
            }),
            take(1)
        );
    }

    get updateCommonSettingsResponse$(): Observable<StoreResponse<void>> {
        return this.actions$.pipe(
            ofType(PlatformSettingsActions.updateCommonSuccess, PlatformSettingsActions.updateCommonFailure),
            map((action) => {
                let result: StoreResponse<void> = { status: 'error' };

                if (action.type === PlatformSettingsActions.updateCommonSuccess.type) {
                    result = new StoreResponseSuccess<void>();
                }

                if (action.type === PlatformSettingsActions.updateCommonFailure.type) {
                    result = new StoreResponseError(action.error);
                }

                return result;
            }),
            take(1)
        );
    }
}
